import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'
import styled from 'styled-components'

import Alert from '../../components/Alert'
import DonationItem from '../../components/DonationItem'
import { HEART_CLICK_DONATE } from '../../constants/dataTracking'
import { useTracking } from '../../hooks'
import { withTranslation } from '../../i18n'
import { memberVisitRequest } from '../../redux/auth/actions'
import { initDonationRequest } from '../../redux/payment/actions'
import {
  getIsDonationDisabled,
  projectSelector
} from '../../redux/project/selectors'
import { fireGtmEvent } from '../../redux/tracking/actions'
import { getColor, getFontSize } from '../../utils/cssUtils'
import {
  detailClickDonateBoxCustom,
  //
  detailClickDonationItem,
  detailReachDonateBox,
  detailReachDonateBoxCustom
} from '../../utils/dataTracker'
import DonationTnc from './DonationTnc'
import FaqLink from './FaqLink'

const Wrapper = styled.div`
  width: 100%;
  border-radius: 4px;
  background-color: #ffffff;
  padding: 32px 24px;
  border: solid 1px transparent;
  transition: border 0.5s linear;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  box-shadow: 0 0 16px 0 rgba(196, 205, 213, 0.5);
  margin-top: 16px;
  :hover {
    box-shadow: 0 0 16px 0 rgba(227, 39, 60, 0.2);
    border: solid 1px ${getColor('theme')};
  }
`
const StyledDonationItem = styled(DonationItem)`
  margin-top: 16px;
  &:first-of-type {
    margin-top: 0;
  }
`
const AlertWrapper = styled.div`
  margin-top: 16px;
`
const DonationCompletedText = styled.div`
  font-size: ${getFontSize('title')};
  font-weight: 600;
  color: ${getColor('grey')};
`
const DonationCount = styled.div`
  float: right;
  font-size: ${getFontSize('headline')};
  font-weight: 600;
  text-align: right;
  color: #202020;
`
const DonationCountText = styled.span`
  font-size: ${getFontSize('body1')};
  color: ${getColor('grey')};
  font-weight: normal;
`

const DonationList = React.memo(({ t, className, projectNo }) => {
  const router = useRouter()

  const projectData = useSelector(
    state => projectSelector(state, projectNo),
    R.equals
  )

  const isDonationDisabled = useSelector(state =>
    getIsDonationDisabled(state, { router, projectNo })
  )

  const dispatch = useDispatch()

  const track = useTracking()

  const [customAmount, setCustomAmount] = useState('')

  const [customDonationAmountError, setCustomDonationAmountError] = useState(
    false
  )

  const generateRedirectPath = (
    type,
    projectNo,
    donationItemId,
    donationAmount
  ) => {
    return `/redirect?project=${projectNo}&donationItemId=${donationItemId}&donationAmount=${
      type === 'custom' ? donationAmount : null
    }`
  }

  const goToEnrollment = async ({
    type,
    name,
    projectNo,
    donationItemId,
    donationAmount,
    donationCount
  }) => {
    if (
      type === 'custom' &&
      (donationAmount < 1000 || donationAmount > 5000000)
    ) {
      setCustomDonationAmountError(true)
      return
    }

    if (type === 'custom') {
      detailClickDonateBoxCustom(projectNo, name, donationCount, donationAmount)
    } else {
      track(
        detailClickDonationItem(projectNo, name, donationAmount, donationCount)
      )
    }

    dispatch(
      fireGtmEvent(HEART_CLICK_DONATE, {
        Content: `[{id: ${projectNo}, quantity: 1, item_price: ${donationAmount} }]`,
        ContentType: 'product',
        ContentID: `["${projectNo}"]`,
        Currency: 'HKD',
        ValueToSum: `${donationAmount}`
      })
    )

    const redirectPath = generateRedirectPath(
      type,
      projectNo,
      donationItemId,
      donationAmount
    )

    dispatch(initDonationRequest(redirectPath))
  }

  const handleJoinDonationEvent = partnerEventId => {
    // set cookie which expires in 5 min
    Cookies.set('hk01_space_int', '01heart', {
      expires: 5 / 60 / 24,
      path: '/',
      domain: process.env.COOKIES_DOMAIN
    })
    dispatch(
      memberVisitRequest(
        `${process.env.DONATION_EVENT_URL}/event/${partnerEventId}/_/ticket`,
        `${process.env.DONATION_EVENT_URL}/event/${partnerEventId}/_/ticket`
      )
    )
  }

  const renderDonationCompletedItem = () => {
    const { totalArchivedDonationCounts } = projectData

    if (totalArchivedDonationCounts) {
      return (
        <Wrapper>
          <DonationCompletedText>
            {t('donationItem.donationCompletedText')}
          </DonationCompletedText>
          <DonationCount>
            {totalArchivedDonationCounts ?? 0}
            <DonationCountText>
              {t('donationItem.donationCount')}
            </DonationCountText>
          </DonationCount>
        </Wrapper>
      )
    }

    return null
  }

  const renderDonationItems = () => {
    const {
      name,
      donationItems,
      donationItemsDonorCount,
      customDonationItemId,
      allowInputDonationAmount,
      allowInputDonationAmountDescription
    } = projectData

    return (
      <>
        {allowInputDonationAmount && (
          <>
            <StyledDonationItem
              key='donationItem_custom'
              error={customDonationAmountError}
              allowCustomAmount
              title={t('donationItem.customAmountDonation')}
              description={allowInputDonationAmountDescription}
              disabled={isDonationDisabled}
              donationCount={donationItemsDonorCount.custom}
              donationItemId={customDonationItemId}
              customAmount={customAmount}
              onPress={() =>
                goToEnrollment({
                  type: 'custom',
                  name,
                  projectNo,
                  donationItemId: customDonationItemId,
                  donationAmount: customAmount * 100,
                  donationCount: donationItemsDonorCount.custom
                })
              }
              onCustomAmountUpdated={amount => {
                setCustomAmount(amount)
              }}
            />
            <Waypoint
              onEnter={() =>
                detailReachDonateBoxCustom(
                  projectNo,
                  name,
                  donationItemsDonorCount.custom
                )
              }
            />
          </>
        )}
        {donationItems.map(item => {
          return (
            <React.Fragment key={item.donationItemId}>
              <StyledDonationItem
                title={item.title}
                amount={item.amount}
                minAmount={item.minAmount}
                description={item.description}
                minAmountDescription={item.minAmountDescription}
                partnerEventId={item.partnerEventId}
                disabled={isDonationDisabled}
                donationCount={donationItemsDonorCount[item.donationItemId]}
                onJoinDonationEvent={() =>
                  handleJoinDonationEvent(item.partnerEventId)
                }
                onPress={() => {
                  goToEnrollment({
                    name,
                    projectNo,
                    donationItemId: item.donationItemId,
                    donationAmount: item.amount,
                    donationCount: donationItemsDonorCount[item.donationItemId]
                  })
                }}
              />
              <Waypoint
                onEnter={() =>
                  detailReachDonateBox(
                    projectNo,
                    name,
                    donationItemsDonorCount[item.donationItemId],
                    item.amount
                  )
                }
              />
            </React.Fragment>
          )
        })}
        {renderDonationCompletedItem()}
      </>
    )
  }

  useEffect(() => {
    setCustomDonationAmountError(false)
    setCustomAmount('')
  }, [projectNo])

  if (R.isEmpty(projectData) || R.isNil(projectData)) return null

  const { allowInputDonationAmount, donationItems } = projectData

  return (
    <div className={className}>
      {customDonationAmountError && (
        <AlertWrapper>
          <Alert showIcon msg={t('project.customDonationAmountError')} />
        </AlertWrapper>
      )}
      {renderDonationItems()}
      {(allowInputDonationAmount || !R.isEmpty(donationItems)) && (
        <DonationTnc />
      )}
      <FaqLink />
    </div>
  )
}, R.equals)

export default withTranslation('common')(DonationList)
